import { useHistory } from "react-router-dom";
import Modal from 'react-modal';
import { v4 as uuid } from 'uuid';
import { useState, useEffect, useContext } from "react";
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { db, timestamp } from "../../firebase/config";
import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { client } from "../../hooks/Client";
import spinner from '../../images/spinner-ripple.svg';

import Tooltip from "../common/Tooltip";
import DeleteModal from "../common/DeleteModal";
import {
  stakeholderTypes,
  allowedPrimaryGroupsByStakeholderType,
  contextLabels,
  processTargetGroup,
} from "../../helpers/dataHelpers/targetgroupCategories";
import SubTargetgroupsCount from "../stakeholders/SubTargetgroupsCount.jsx";
import DragIconTableData from "../common/DragIconTableData.jsx";
import Table from "../common/table/Table.jsx";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import AddTargetgroupFlow from "./add/AddTargetgroupFlow.jsx";
import LinkedBadge from "../common/LinkedBadge.jsx";
import useSyncedLinks from "../../hooks/useSyncedLinks";
import useParentPortfolios from "../../hooks/useParentPortfolios";
import softDeleteDoc from '../../helpers/softDeleteDoc';

// Dutch labels for display
const stakeholderTypeLabels = {
  individual: 'Individu',
  household: 'Huishouden',
  community: 'Gemeenschap',
  organization: 'Organisatie',
  government: 'Overheid',
  other: 'Overig',
}

const primaryTargetGroupLabels = {
  children: 'Kinderen',
  youth: 'Jongeren',
  adults: 'Volwassenen',
  older_people: 'Ouderen',
  people_with_disabilities: 'Mensen met een beperking',
  people_with_psychological_vulnerability: 'Mensen met psychische kwetsbaarheid',
  people_with_distance_to_labour_market: 'Mensen met afstand tot arbeidsmarkt',
  people_in_poverty: 'Mensen in armoede',
  people_with_debt: 'Mensen met schulden',
  people_with_migration_background: 'Mensen met migratieachtergrond',
  refugees_status_holders_asylum_seekers: 'Vluchtelingen / statushouders / asielzoekers',
  homeless_or_houseless_people: 'Daklozen / thuislozen',
  caregivers: 'Mantelzorgers',
  families_households: 'Gezinnen / huishoudens',
  neighbourhood_residents: 'Buurtbewoners',
  organizations: 'Organisaties',
  governments: 'Overheden',
  other: 'Overig',
}

const contextLabelLabels = {
  low_income: 'Laag inkomen',
  unemployed: 'Werkloos',
  long_term_unemployed: 'Langdurig werkloos',
  neet: 'NEET (geen school/werk)',
  single_parent: 'Alleenstaande ouder',
  socially_isolated: 'Sociaal geïsoleerd',
  lonely: 'Eenzaam',
  physical_disability: 'Lichamelijke beperking',
  intellectual_disability: 'Verstandelijke beperking',
  mild_intellectual_disability: 'Lichte verstandelijke beperking',
  chronic_illness: 'Chronische ziekte',
  mental_health_issues: 'Psychische problematiek',
  addiction: 'Verslaving',
  stress_burnout: 'Stress / burn-out',
  language_barrier: 'Taalbarrière',
  low_literacy: 'Laaggeletterdheid',
  digital_exclusion: 'Digitale uitsluiting',
  migration_history: 'Migratiegeschiedenis',
  newcomer: 'Nieuwkomer',
  refugee_background: 'Vluchtelingenachtergrond',
  undocumented: 'Ongedocumenteerd',
  homeless: 'Dakloos',
  houseless: 'Thuisloos',
  housing_insecurity: 'Onzekere woonsituatie',
  youth_at_risk: 'Jongere in kwetsbare situatie',
  ex_offender: 'Ex-gedetineerde',
  victim_of_domestic_violence: 'Slachtoffer huiselijk geweld',
  other: 'Overig',
}

const EMPTY_FORM = {
  stakeholderType: '',
  primaryTargetGroups: [],
  contextLabels: [],
}

/**
 * Reusable CRUD body for Doelgroepen (the target-group step). Rendered both on
 * the dedicated Stakeholders route and inline in the TOC navigator (F7). Owns
 * its table, 3-step taxonomy wizard, portfolio-subscribe + delete modals. The
 * route wrapper keeps the page chrome (PageHeader, Prev/Next, ScrollToTop).
 */
const StakeholderManager = () => {
  // Context
  const [, setSaved] = useContext(SavedIcon);

  // Local state
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDocid, setDeleteDocid] = useState('');
  const [deleteName, setDeleteName] = useState('');
  const [openAddTargetgroupModal, setOpenAddTargetgroupModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [targetgroupName, setTargetgroupName] = useState('');
  const [targetgroupDescription, setTargetgroupDescription] = useState('');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [dragArray, setDragArray] = useState([]);
  const [showAddContainer, setShowAddContainer] = useState(false);

  // Firestore
  const stakeholdersFirestore = useFirestoreOrderBy('Stakeholders', 'Position', 'asc');

  // Portfolio-paired doelgroepen get a link badge (Synchronisations rows).
  const syncedLinks = useSyncedLinks();
  const { portfolioNameById } = useParentPortfolios();
  const linkedBadgeFor = (id) =>
    id && syncedLinks[id]
      ? <LinkedBadge portfolioName={portfolioNameById[syncedLinks[id]]} />
      : null;

  // For router
  const history = useHistory();

  // Modal settings
  Modal.setAppElement('#root');
  const modalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      minWidth: '400px',
      maxWidth: '560px',
      width: '90%',
    },
  };

  // Whenever Firestore data changes, update dragArray state
  useEffect(() => {
    setDragArray(stakeholdersFirestore);
  }, [stakeholdersFirestore]);

  // Reset modal state when closed
  const openModal = () => {
    setModalMode('add');
    setEditingStakeholder(null);
    setTargetgroupName('');
    setTargetgroupDescription('');
    setForm(EMPTY_FORM);
    setStep(1);
    setOpenAddTargetgroupModal(true);
  };

  const openEditModal = (stakeholder) => {
    setModalMode('edit');
    setEditingStakeholder(stakeholder);
    setTargetgroupName(stakeholder.Organisation || '');
    setTargetgroupDescription(stakeholder.Description || '');
    setForm({
      stakeholderType: stakeholder.stakeholderType || '',
      primaryTargetGroups: stakeholder.primaryTargetGroups || [],
      contextLabels: stakeholder.contextLabels || [],
    });
    setStep(1);
    setOpenAddTargetgroupModal(true);
  };

  const closeModal = () => {
    setOpenAddTargetgroupModal(false);
  };

  // DRAG & DROP: reorder items and update Firestore
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArray(items);

    items.forEach((item, idx) => {
      db.collection('Stakeholders')
        .doc(item.docid)
        .update({ Position: idx + 1 });
    });

    setSaved('flex');
  };

  // ========== CRUD handlers ==========

  const deleteModalHandler = (e) => {
    const docid = e.currentTarget.dataset.docid;
    const delName = e.currentTarget.dataset.deletename;
    setDeleteDocid(docid);
    setDeleteName(delName);
    setDeleteModal(true);
  };

  const deleteStakeholder = () => {
    softDeleteDoc({ collection: 'Stakeholders', docid: deleteDocid })
      .then(() => {
        setDeleteModal(false);
        setSaved('flex');
      });
  };

  const addStakeholder = () => {
    setLoading(true);
    const targetgroupID = uuid();
    const docRef = db.collection('Stakeholders').doc();

    const processed = processTargetGroup(form);

    docRef.set({
      docid: docRef.id,
      Organisation: targetgroupName,
      Description: targetgroupDescription,
      ID: targetgroupID,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Position: stakeholdersFirestore.length + 1,
      // New taxonomy fields — both shapes: the UI reads the array,
      // benchmark/portfolio-sync read the single primary group (ADR-009).
      stakeholderType: processed.stakeholderType,
      primaryTargetGroup: processed.primaryTargetGroup,
      primaryTargetGroups: processed.primaryTargetGroups,
      contextLabels: processed.contextLabels,
      analysisTags: processed.analysisTags,
      impactMapping: processed.impactMapping,
    })
    .then(() => {
      setLoading(false);
      closeModal();
      setSaved('flex');
    });
  };

  const updateStakeholder = () => {
    setLoading(true);
    const processed = processTargetGroup(form);

    db.collection('Stakeholders')
      .doc(editingStakeholder.docid)
      .update({
        Organisation: targetgroupName,
        Description: targetgroupDescription,
        stakeholderType: processed.stakeholderType,
        primaryTargetGroup: processed.primaryTargetGroup,
        primaryTargetGroups: processed.primaryTargetGroups,
        contextLabels: processed.contextLabels,
        analysisTags: processed.analysisTags,
        impactMapping: processed.impactMapping,
      })
      .then(() => {
        setLoading(false);
        closeModal();
        setSaved('flex');
      });
  };

  const editNameHandler = (e) => {
    const docid = e.target.dataset.docid;
    const name = e.target.value;
    db.collection('Stakeholders')
      .doc(docid)
      .update({ Organisation: name })
      .then(() => setSaved('flex'));
  };

  const editDescriptionHandler = (e) => {
    const docid = e.target.dataset.docid;
    const description = e.target.value;
    db.collection('Stakeholders')
      .doc(docid)
      .update({ Description: description })
      .then(() => setSaved('flex'));
  };

  // ========== Step form handlers ==========

  const selectStakeholderType = (type) => {
    setForm({ stakeholderType: type, primaryTargetGroups: [], contextLabels: [] });
  };

  const togglePrimaryGroup = (key) => {
    setForm(prev => {
      const already = prev.primaryTargetGroups.includes(key);
      if (already) {
        return { ...prev, primaryTargetGroups: prev.primaryTargetGroups.filter(k => k !== key) };
      }
      if (prev.primaryTargetGroups.length >= 3) return prev; // max 3
      return { ...prev, primaryTargetGroups: [...prev.primaryTargetGroups, key] };
    });
  };

  const toggleContextLabel = (key) => {
    setForm(prev => {
      const already = prev.contextLabels.includes(key);
      if (already) {
        return { ...prev, contextLabels: prev.contextLabels.filter(k => k !== key) };
      }
      if (prev.contextLabels.length >= 5) return prev; // max 5
      return { ...prev, contextLabels: [...prev.contextLabels, key] };
    });
  };

  const canProceedStep1 = form.stakeholderType !== '';
  const canProceedStep2 = form.primaryTargetGroups.length > 0;
  const allowedGroups = allowedPrimaryGroupsByStakeholderType[form.stakeholderType] || [];

  const tableColumns = [
    { key: 'drag', label: '', searchable: false },
    { key: 'organisation', label: 'DOELGROEP' },
    { key: 'description', label: 'BESCHRIJVING', searchable: false },
    { key: 'type', label: 'TYPE', searchable: false },
    { key: 'subtargetgroups', label: 'SUB-DOELGROEP(EN)', searchable: false },
    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
  ];

  return (
    <>
      <ReadOnlyGuard edit="strategy.edit">
        {(canEdit) => (
      <Table
        columns={tableColumns}
        items={dragArray || []}
        getItemKey={(stakeholder) => stakeholder.ID}
        renderRow={(stakeholder) => (
          <>
            <DragIconTableData targetgroup={stakeholder}/>
            <td>
              <Tooltip
                content={stakeholder.Organisation}
                width="80%"
                top="-60px"
              >
                <input
                  type="text"
                  data-docid={stakeholder.docid}
                  defaultValue={stakeholder.Organisation}
                  placeholder='Omschrijving van doelgroep'
                  onChange={editNameHandler}
                />
              </Tooltip>
              {linkedBadgeFor(stakeholder.ID)}
            </td>
            <td className="common-table__cell--description">
              <Tooltip content={stakeholder.Description || ''} width="80%" top="-60px">
                <textarea
                  className="common-table__description-textarea"
                  data-docid={stakeholder.docid}
                  defaultValue={stakeholder.Description || ''}
                  placeholder="Beschrijving"
                  onChange={editDescriptionHandler}
                />
              </Tooltip>
            </td>
            <td>
              <div id='indicator-effect-icons-container'>
                <span>
                  {stakeholder.stakeholderType
                    ? stakeholderTypeLabels[stakeholder.stakeholderType] || stakeholder.stakeholderType
                    : stakeholder.Categorie || '—'}
                </span>
                <CreateOutlinedIcon
                  style={{ fontSize: '16px', cursor: 'pointer', marginLeft: '6px', color: '#999' }}
                  onClick={() => openEditModal(stakeholder)}
                />
              </div>
            </td>
            <td>
              <div id='indicator-effect-icons-container'>
                  <Tooltip content={'Sub-doelgroep toevoegen'} top='-60px' width={'30px'}>
                      <AddCircleOutlineOutlinedIcon className='table-delete-icon' onClick={() => history.push(`/${client}/subtargetgroups/${stakeholder.ID}`)}/>
                  </Tooltip>
                  <Tooltip content={'Aantal sub-doelgroepen'} top='-60px' width='30px'>
                      <p className="common-table__count-pill"><SubTargetgroupsCount targetgroupId={stakeholder.ID} /></p>
                  </Tooltip>
              </div>
            </td>
            <td>
              {canEdit && (
                <DeleteOutlinedIcon
                  className='table-delete-icon'
                  data-docid={stakeholder.docid}
                  data-deletename={stakeholder.Organisation}
                  onClick={deleteModalHandler}
                />
              )}
            </td>
          </>
        )}
        searchAccessor={(stakeholder) => stakeholder.Organisation}
        searchPlaceholder="Zoeken op doelgroep"
        emptyMessage="Geen doelgroepen gevonden."
        draggable={true}
        droppableId="stakeholders"
        onDragEnd={handleDragEnd}
        addRow={canEdit ? {
          toolTipContent: 'Voeg een doelgroep toe',
          onClick: () => setShowAddContainer(true),
        } : undefined}
      />
        )}
      </ReadOnlyGuard>

      <AddTargetgroupFlow
        open={showAddContainer}
        setOpen={setShowAddContainer}
        onOwnAdd={openModal}
      />

      {/* Delete modal */}
      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteStakeholder}
        deleteName={deleteName}
        softDelete
      />

      {/* Add stakeholder modal — 3-step taxonomy form */}
      <Modal
        isOpen={openAddTargetgroupModal}
        onRequestClose={closeModal}
        style={modalStyles}
        contentLabel="Doelgroep toevoegen"
      >
        <div>
          <div id='delete-modal-text-container'>

            {/* Step indicator */}
            <p style={{ color: '#999', fontSize: '13px', marginBottom: '4px' }}>
              Stap {step} van 3
            </p>

            {/* ── Step 1: Name + stakeholderType ── */}
            {step === 1 && (
              <>
                <h1>{modalMode === 'edit' ? 'Doelgroep aanpassen' : 'Doelgroep toevoegen'}</h1>
                <p>Geef de doelgroep een naam</p>
                <input
                  type="text"
                  placeholder='Schrijf hier de naam van de doelgroep'
                  value={targetgroupName}
                  onChange={(e) => setTargetgroupName(e.target.value)}
                />
                <p>Beschrijving (optioneel)</p>
                <textarea
                  rows="3"
                  placeholder='Beschrijf de doelgroep'
                  value={targetgroupDescription}
                  onChange={(e) => setTargetgroupDescription(e.target.value)}
                />
                <p>Wat voor type stakeholder is dit?</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {stakeholderTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => selectStakeholderType(type)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid #ccc',
                        background: form.stakeholderType === type ? '#2196f3' : '#fff',
                        color: form.stakeholderType === type ? '#fff' : '#333',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      {stakeholderTypeLabels[type]}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ── Step 2: primaryTargetGroups ── */}
            {step === 2 && (
              <>
                <h1>Primaire doelgroepen</h1>
                <p>Selecteer maximaal 3 primaire doelgroepen</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {allowedGroups.map(key => (
                    <button
                      key={key}
                      onClick={() => togglePrimaryGroup(key)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid #ccc',
                        background: form.primaryTargetGroups.includes(key) ? '#2196f3' : '#fff',
                        color: form.primaryTargetGroups.includes(key) ? '#fff' : '#333',
                        cursor: 'pointer',
                        fontSize: '13px',
                        opacity: !form.primaryTargetGroups.includes(key) && form.primaryTargetGroups.length >= 3 ? 0.4 : 1,
                      }}
                    >
                      {primaryTargetGroupLabels[key] || key}
                    </button>
                  ))}
                </div>
                <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                  {form.primaryTargetGroups.length}/3 geselecteerd
                </p>
              </>
            )}

            {/* ── Step 3: contextLabels ── */}
            {step === 3 && (
              <>
                <h1>Context kenmerken</h1>
                <p>Selecteer optioneel tot 5 kenmerken die de context verder beschrijven</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {contextLabels.map(key => (
                    <button
                      key={key}
                      onClick={() => toggleContextLabel(key)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: '1px solid #ccc',
                        background: form.contextLabels.includes(key) ? '#2196f3' : '#fff',
                        color: form.contextLabels.includes(key) ? '#fff' : '#333',
                        cursor: 'pointer',
                        fontSize: '13px',
                        opacity: !form.contextLabels.includes(key) && form.contextLabels.length >= 5 ? 0.4 : 1,
                      }}
                    >
                      {contextLabelLabels[key] || key}
                    </button>
                  ))}
                </div>
                <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                  {form.contextLabels.length}/5 geselecteerd
                </p>
              </>
            )}

          </div>

          {/* Buttons */}
          <div id='delete-modal-button-container'>
            <button
              id='modal-cancel-button'
              onClick={step === 1 ? closeModal : () => setStep(s => s - 1)}
              style={{ display: loading ? 'none' : 'block' }}
            >
              {step === 1 ? 'Annuleren' : 'Vorige'}
            </button>

            {step < 3 && (
              <button
                id='modal-save-button'
                onClick={() => setStep(s => s + 1)}
                disabled={step === 1 ? (!canProceedStep1 || !targetgroupName.trim()) : !canProceedStep2}
                style={{ display: loading ? 'none' : 'block' }}
              >
                Volgende
              </button>
            )}

            {step === 3 && (
              <button
                id='modal-save-button'
                onClick={modalMode === 'edit' ? updateStakeholder : addStakeholder}
                style={{ display: loading ? 'none' : 'block' }}
              >
                Opslaan
              </button>
            )}

            <img
              src={spinner}
              alt=""
              style={{ display: loading ? 'block' : 'none' }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StakeholderManager;
